import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

axios.defaults.withCredentials = true;
localStorage.removeItem('okni_token');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setError(null);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const register = async (data) => {
    try {
      const response = await api.post('/auth/register', data);
      setUser(response.data.user);
      setError(null);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur lors de l\'inscription';
      setError(message);
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      setUser(response.data.user);
      setError(null);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Email ou mot de passe incorrect';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    setUser(null);
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // OAuth: cookie already set by backend
  const loginWithToken = (_accessToken, userData) => {
    setUser(userData);
    setError(null);
  };

  const updateUser = async (data) => {
    try {
      const response = await api.put('/auth/me', data);
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur lors de la mise à jour';
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = {
    user,
    token: user ? 'cookie' : null,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    loginWithToken,
    logout,
    updateUser,
    refreshUser,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
